<template>
  <div v-if="footerRows.length" :class="ns.e('footer')">
    <div
      v-for="(footRow, footIndex) in footerRows"
      :key="footIndex"
      :class="ns.e('row')"
      :style="tableRowStyle"
    >
      <div
        v-for="col in columns"
        :key="col.id"
        :class="[
          ns.e('cell'),
          ns.e('footer-cell'),
          { 'fixed--hidden': isColumnHidden(col) },
        ]"
        :style="cellStyle(col)"
      >
        {{ getFooterCellValue(footRow, col) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import { addUnit } from '@zoryn/utils'
import { getColumnAlignStyle, resolveColumnMinWidth, resolveColumnWidth } from '../column/meta'
import { isFixedColumnHidden } from '../utils/fixed-column'
import type { CSSProperties } from 'vue'
import type { DataGrid, GridColumnInfo, GridFooterRow } from '../types'
import type { GridFixedType } from '../utils/fixed-column'

const props = withDefaults(
  defineProps<{
    fixedType?: GridFixedType
    overflowX?: boolean
    columns?: GridColumnInfo[]
    contentWidth?: number
  }>(),
  {
    fixedType: '',
    overflowX: false,
    columns: undefined,
  }
)

const ns = useNamespace('grid')
const $grid = inject('$grid') as DataGrid

const columns = computed(
  () => props.columns ?? $grid.internalData.visibleColumns
)
const footerRows = computed(() => $grid.reactData.footerTableData)
const tableWidth = computed(() =>
  columns.value.reduce((sum, col) => sum + resolveColumnWidth(col), 0)
)
const tableRowStyle = computed<CSSProperties>(() => ({
  minWidth: '100%',
  width: `${props.contentWidth ?? tableWidth.value}px`,
}))

function isColumnHidden(col: GridColumnInfo) {
  return isFixedColumnHidden(
    col,
    props.fixedType,
    props.overflowX,
    $grid.internalData.visibleColumns
  )
}

function cellStyle(col: GridColumnInfo): CSSProperties {
  const w = resolveColumnWidth(col)
  return {
    width: addUnit(w),
    minWidth: addUnit(resolveColumnMinWidth(col)),
    flex: `0 0 ${addUnit(w)}`,
    ...getColumnAlignStyle(col.align),
  }
}

function getFooterCellValue(footRow: GridFooterRow, col: GridColumnInfo) {
  if (footRow == null || typeof footRow !== 'object') {
    return footRow ?? ''
  }
  if (!col.field) {
    return ''
  }
  const val = footRow[col.field]
  return val == null ? '' : String(val)
}
</script>
